
import { supabase } from '@/integrations/supabase/client';

export interface InternalUser {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  role: string;
  profile_photo_url?: string;
}

const normalizeUserSearchQuery = (query: string) => query.trim().replace(/^@+/, '').toLowerCase();

export const internalUserService = {
  async searchUsers(query: string): Promise<InternalUser[]> {
    console.log('=== INTERNAL USER SERVICE SEARCH ===');
    console.log('Search query:', query);
    
    if (!query || query.trim().length < 1) {
      console.log('Empty query, returning empty array');
      return [];
    }

    const cleanQuery = normalizeUserSearchQuery(query);
    console.log('Clean query:', cleanQuery);

    if (!cleanQuery) {
      console.log('Normalized query is empty, returning empty array');
      return [];
    }

    try {
      // Fetch all active internal users with their profiles using a joined query
      const { data, error } = await supabase
        .from('internal_users')
        .select(`
          id, 
          username, 
          role,
          internal_user_profiles(first_name, last_name, profile_photo_url)
        `)
        .eq('is_active', true)
        .limit(100);

      if (error) {
        console.error('Error fetching internal users:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.log('No internal users found');
        return [];
      }

      console.log('Internal users fetched:', data.length);

      // Filter in JavaScript to check username, first_name, and last_name
      const filteredResults = data.filter(user => {
        const profile = Array.isArray(user.internal_user_profiles) 
          ? user.internal_user_profiles[0] 
          : user.internal_user_profiles;
        
        const username = user.username?.toLowerCase() || '';
        const firstName = (profile?.first_name || '').toLowerCase();
        const lastName = (profile?.last_name || '').toLowerCase();
        
        return username.includes(cleanQuery) || 
               firstName.includes(cleanQuery) || 
               lastName.includes(cleanQuery);
      });

      console.log('Filtered results:', filteredResults.length);

      // Transform to InternalUser format
      const results: InternalUser[] = filteredResults.map(user => {
        const profile = Array.isArray(user.internal_user_profiles) 
          ? user.internal_user_profiles[0] 
          : user.internal_user_profiles;
        return {
          id: user.id,
          username: user.username,
          first_name: profile?.first_name || user.username,
          last_name: profile?.last_name || '',
          role: user.role,
          profile_photo_url: profile?.profile_photo_url
        };
      });

      console.log('Final internal user results:', results);
      return results;
    } catch (error) {
      console.error('Exception in searchUsers:', error);
      return [];
    }
  },

  async getAllUsers(): Promise<InternalUser[]> {
    try {
      // Use a joined query for better performance - single database call
      const { data, error } = await supabase
        .from('internal_users')
        .select(`
          id, 
          username, 
          role,
          internal_user_profiles(first_name, last_name, profile_photo_url)
        `)
        .eq('is_active', true)
        .limit(100);

      if (error) {
        console.error('Error fetching internal users:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Transform the joined data
      const results: InternalUser[] = data.map(user => {
        const profile = Array.isArray(user.internal_user_profiles) 
          ? user.internal_user_profiles[0] 
          : user.internal_user_profiles;
        return {
          id: user.id,
          username: user.username,
          first_name: profile?.first_name || user.username,
          last_name: profile?.last_name || '',
          role: user.role,
          profile_photo_url: profile?.profile_photo_url
        };
      });

      return results;
    } catch (error) {
      console.error('Exception in getAllUsers:', error);
      return [];
    }
  }
};
